import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { AccountStore } from "../../../services/AccountStore";
import { AccountOrg, GetAccountOrgsQuery, AccountOrgsSummary } from "@whatslly/types/GetAccountOrgs";
import { modalService } from "@whatslly/ui-components/modals";
import { AssignOrgsToAccountModal } from "./Modals/AssignOrgsToAccountModal";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { RemoveOrgFromAccountModal } from "./Modals/RemoveOrgFromAccountModal";
import { i18n } from "@whatslly/commons/i18nService";
import { ChangeOrganizationNameModal } from "../../../components/Filters/components/OrgSelect/modals/ChangeOrganizationNameModal";
import { FiltersStore } from "../../../services/filtersStore";
import { OrgConfigsStore } from "../../../services/OrgConfigsStore";
import { ORG_IDENTITY_MODAL_CONFIG_KEYS } from "@whatslly/commons/OrgConfigsFeatures";
import { ORG_TYPE_OPTIONS } from "@whatslly/commons/constants/organizationTypes";
import type { OrgConfigs } from "@whatslly/types/OrgConfigs";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import type { IAccountOrganizationsStore } from "./IAccountOrganizationsStore";
import type { TableSort } from "@whatslly/ui-components/v2/Tables/Table/TableTypes";

@injectable()
export class AccountOrganizationsStore implements IAccountOrganizationsStore {
  private _data: { orgs: AccountOrg[]; summary: AccountOrgsSummary | null } = { orgs: [], summary: null };
  private _orgsPromise: IPromiseBasedObservable<AccountOrg[]>;
  private _sort: TableSort = { keyInsideItem: "name", direction: "asc" };

  constructor(
    @inject(AccountStore) private readonly accountStore: AccountStore,
    @inject(FiltersStore) private filtersStore: FiltersStore,
    @inject(InjectOrganizationStore) private organizationsStore: IOrganizationStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore
  ) {
    this._orgsPromise = fromPromise(Promise.resolve([])); // Initialize with empty promise

    makeObservable<AccountOrganizationsStore, "_data" | "_orgsPromise" | "_sort">(this, {
      _data: observable,
      _orgsPromise: observable,
      _sort: observable,
      orgs: computed,
      summary: computed,
      sort: computed,
      assignOrgsToAccount: action.bound,
      setSort: action.bound,
    });

    reaction(
      () => [this.accountStore.accountId, this._sort.keyInsideItem, this._sort.direction],
      () => this.initData(),
      { fireImmediately: true }
    );
  }

  private initData() {
    runInAction(() => {
      if (!this.accountStore.accountId) return;
      this._orgsPromise = fromPromise(this._getData(true));
    });
  }

  private async _getData(replaceData?: boolean) {
    const res = await this.accountStore.getAccountOrgs(this.accountStore.accountId, {
      sortBy: this._sort.keyInsideItem as GetAccountOrgsQuery["sortBy"],
      sort: this._sort.direction,
    });
    if (!res.success) {
      throw new Error(res.error);
    }

    this._data.orgs = replaceData ? res.orgs : this._data.orgs.concat(res.orgs);
    this._data.summary = res.summary;
    return this._data.orgs;
  }

  get orgs() {
    return this._orgsPromise;
  }

  get summary() {
    return this._data.summary;
  }

  get sort() {
    return this._sort;
  }

  setSort(sort: TableSort) {
    this._sort = sort;
  }

  async assignOrgsToAccount() {
    const result = await modalService.openModal(AssignOrgsToAccountModal).promise;
    if (!result) return;

    const res = await this.accountStore.updateAccountOrgs(result.orgIds);

    if (!res.success) {
      modalService.openModal(Toast, {
        text: res.error ?? i18n.t("account_admin.account_orgs.assign_orgs_modal.error_message"),
        type: "error",
      });

      return;
    }

    modalService.openModal(Toast, {
      text: i18n.t("account_admin.account_orgs.assign_orgs_modal.success_message"),
      type: "regular",
    });
    this.initData();
  }

  async createAccountOrg() {
    if (!this.accountStore.accountId) return;
    const res = await this.organizationsStore.createNewOrg(this.accountStore.accountId);
    if (res) {
      this.initData();
    }
  }

  async changeOrgName(org: AccountOrg) {
    this.filtersStore.orgId = org.id;
    const keys = [...ORG_IDENTITY_MODAL_CONFIG_KEYS] as (keyof OrgConfigs)[];
    const cfgRes = await this.orgConfigsStore.loadConfigs(keys, { orgId: org.id });
    const initialIdentityFromServer = cfgRes.success && cfgRes.configs ? cfgRes.configs : {};
    const orgTypeFromCfg = initialIdentityFromServer.orgType;
    const orgTypeOption = orgTypeFromCfg ? ORG_TYPE_OPTIONS.find((o) => o.value === orgTypeFromCfg) : undefined;

    const newData = await modalService.openModal(ChangeOrganizationNameModal, {
      orgName: org.name,
      orgDescription: org.description,
      orgType: orgTypeOption,
      initialIdentityFromServer,
    }).promise;
    if (!newData?.name || !newData?.orgType) return;
    const res = await this.organizationsStore.changeOrgName({
      newName: newData.name,
      description: newData.description,
      orgType: newData.orgType.value,
      isActive: newData.isActive,
      isPOC: newData.isPOC,
      POCExpiration: newData.POCExpiration,
      org_partner: newData.org_partner,
      defaultLanguage: newData.defaultLanguage,
      serverlocation: newData.serverlocation,
      available_crm: newData.available_crm,
    });
    if (res) {
      this.initData();
    }
  }

  async removeOrgFromAccount(orgId: string) {
    const result = await modalService.openModal(RemoveOrgFromAccountModal).promise;
    if (!result) return;

    const res = await this.accountStore.unassignAccountOrg(orgId);

    if (!res.success) {
      modalService.openModal(Toast, {
        text: res.error ?? i18n.t("account_admin.account_orgs.remove_org_modal.error_message"),
        type: "error",
      });
      return;
    }

    modalService.openModal(Toast, {
      text: i18n.t("account_admin.account_orgs.remove_org_modal.success_message"),
      type: "regular",
    });
    this.initData();
  }
}
